import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ImageField } from "./ImageField";
import { EventsEditor } from "./EventsEditor";
import { api, apiError } from "../../lib/api";

const LABELS = {
  hero_title: "Titolo hero (una riga per linea)", hero_subtitle: "Sottotitolo hero", hero_eyebrow: "Etichetta hero",
  hero_cta_primary: "CTA principale", hero_cta_secondary: "CTA secondaria", hero_claim: "Claim",
  ga4_id: "ID Google Analytics 4 (G-XXXX)", gtm_id: "ID Google Tag Manager (GTM-XXXX)", conversion_event: "Evento conversione GA4/GTM all'invio del modulo",
  meta_pixel_id: "ID Meta Pixel (solo numeri)", events: "Eventi (stile PixelYourSite)",
  event_name: "Nome evento (es. Lead, Contact, Schedule o un nome a scelta)",
  trigger: "Quando si attiva", target: "Dove (testo del pulsante, link es. https://wa.me, selettore CSS, oppure pagina es. /programma)",
  platforms: "Piattaforme (tutte, oppure meta, ga4, gtm separati da virgola)",
  whatsapp: "Numero WhatsApp (solo cifre con prefisso, es. 393...)", whatsapp_message: "Messaggio WhatsApp precompilato",
  legal_name: "Ragione sociale", vat: "Partita IVA", og_image: "Immagine condivisione social",
  email: "Email", title: "Titolo", description: "Descrizione", text: "Testo", name: "Nome", image: "Immagine",
};
const label = (k) => LABELS[k] || k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const inputCls = "w-full rounded-lg bg-white/[0.04] border hairline px-3 py-2 text-sm text-white outline-none focus:border-[#4fc3f7]/60";

const blank = (tpl) => Object.fromEntries(Object.entries(tpl).map(([k, v]) => [k, Array.isArray(v) ? [] : k === "trigger" ? "click" : k === "platforms" ? "tutte" : ""]));

function renderValue(k, v, onChange, path) {
  const tid = `field-${path}`;
  if (k === "events") return <EventsEditor value={v} onChange={onChange} />;
  if (k === "trigger") {
    return (
      <select data-testid={tid} className={inputCls} value={v} onChange={(e) => onChange(e.target.value)}>
        <option value="invio_modulo">Invio modulo consulenza</option>
        <option value="click">Click su pulsante o link</option>
        <option value="pagina">Visita di una pagina</option>
      </select>
    );
  }
  if (k.includes("image")) return <ImageField value={v} onChange={onChange} testId={tid} />;
  if (Array.isArray(v) && (v.length === 0 || typeof v[0] === "string")) {
    return <textarea data-testid={tid} rows={Math.max(3, v.length)} className={inputCls} value={v.join("\n")}
      onChange={(e) => onChange(e.target.value.split("\n"))} placeholder="Un elemento per riga" />;
  }
  if (Array.isArray(v)) {
    return (
      <div className="space-y-3">
        {v.map((item, i) => (
          <div key={i} className="rounded-xl border hairline p-4 bg-white/[0.02] space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-mono text-[11px] text-slate-400">#{i + 1}</span>
              <button type="button" onClick={() => onChange(v.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400" aria-label="Rimuovi"><Trash2 className="w-4 h-4" /></button>
            </div>
            {Object.keys(item).map((kk) => renderRow(kk, item[kk], (nv) => onChange(v.map((it, j) => (j === i ? { ...it, [kk]: nv } : it))), `${path}-${i}-${kk}`))}
          </div>
        ))}
        <button type="button" data-testid={`${tid}-add`} onClick={() => onChange([...v, blank(v[0] || { title: "" })])}
          className="h-9 px-3 rounded-lg border hairline text-sm text-white inline-flex items-center gap-2 hover:bg-white/5"><Plus className="w-4 h-4" />Aggiungi</button>
      </div>
    );
  }
  const long = String(v).length > 70 || String(v).includes("\n");
  return long
    ? <textarea data-testid={tid} rows={3} className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} />
    : <input data-testid={tid} className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} />;
}

function renderRow(k, v, onChange, path) {
  return (
    <div key={k} className="block">
      <span className="block text-xs text-slate-400 mb-1.5">{label(k)}</span>
      {renderValue(k, v, onChange, path)}
    </div>
  );
}

export const ContentEditor = ({ section, initial, hint }) => {
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setData(initial); }, [initial]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${section}`, data);
      toast.success("Modifiche salvate");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid={`editor-${section}`} className="space-y-5">
      {hint && <p className="text-sm text-slate-400">{hint}</p>}
      <div className="grid gap-5">
        {Object.keys(data || {}).map((k) => renderRow(k, data[k], (nv) => setData((d) => ({ ...d, [k]: nv })), `${section}-${k}`))}
      </div>
      <div className="sticky bottom-4 flex justify-end">
        <button data-testid={`save-${section}`} onClick={save} disabled={saving}
          className="h-11 px-5 rounded-full bg-[#ff5500] hover:bg-[#ff6a1f] text-white font-medium inline-flex items-center gap-2 shadow-xl shadow-black/40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Salva
        </button>
      </div>
    </div>
  );
};
